import React from 'react';
import { formatDetailedDate, formatCurrencyForDisplay } from '@/lib/utils';
import type { TableAvailabilitySlot } from '@/features/booking/types/tableBookingTypes';


interface Table {
  number: string;
  size: number;
}

interface TableLayoutProps {
  allTablesInVenue: Table[];
  tableAvailability: Record<string, TableAvailabilitySlot[]>;
  selectedBookings: Record<string, TableAvailabilitySlot>;
  onTableClick: (tableNumber: string) => void;
  selectedDate: string | null;
  businessTimeZone?: string;
}

const TableLayout: React.FC<TableLayoutProps> = ({
  allTablesInVenue,
  tableAvailability,
  selectedBookings,
  onTableClick,
  selectedDate,
  businessTimeZone
}) => {
  // Get club handle from first available table slot (all slots should have the same business)
  const formatPrice = (amount: number, currency: string = 'USD') => {
    return formatCurrencyForDisplay(amount, currency);
  };

  return (
    <div className="bg-gray-900/30 border border-gray-800 rounded-xl p-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold mb-1">
          2. Select Your Table(s)
        </h2>
        <p className="text-gray-400">
          {selectedDate ? `Available tables for ${formatDetailedDate(selectedDate, businessTimeZone)}` : 'Choose a date to see available tables'}
        </p>
      </div>
      
      <div className="max-w-3xl mx-auto">
        <div className="relative">
          <div className="w-full h-16 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-lg mb-8 flex items-center justify-center">
            <span className="text-purple-400 font-semibold tracking-widest">STAGE / DJ BOOTH</span>
          </div>
          
          <div className="grid grid-cols-6 sm:grid-cols-8 gap-4 mb-8">
            {allTablesInVenue.map(table => {
              const isAvailable = !!tableAvailability[table.number];
              const isSelected = !!selectedBookings[table.number];
              const tableSlotInfo = isAvailable ? tableAvailability[table.number][0] : null;
              
              return (
                <div key={table.number} className="relative group">
                  <button 
                    onClick={() => onTableClick(table.number)}
                    disabled={!isAvailable}
                    className={`w-full aspect-square flex flex-col items-center justify-center rounded-lg border-2 font-bold text-sm transition-all duration-200 ${
                      isSelected 
                        ? 'bg-blue-600/40 border-blue-500 text-white scale-110 shadow-lg shadow-blue-500/20' 
                        : isAvailable 
                        ? 'bg-gray-800 border-gray-700 cursor-pointer hover:bg-gray-700 hover:border-gray-500 hover:scale-105' 
                        : 'bg-gray-900 border-gray-800 text-gray-600 cursor-not-allowed opacity-50'
                    }`}
                  >
                    <span className="text-lg">{table.number}</span>
                    <span className="text-xs opacity-80">{table.size}p</span>
                  </button>
                  
                  {isAvailable && tableSlotInfo && !isSelected && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10 w-48">
                      <div className="bg-black border border-gray-700 rounded-lg px-3 py-2 text-xs text-left">
                        <div className="font-bold text-white">{tableSlotInfo.event_booking_category.booking_category.name}</div>
                        <div className="text-gray-400 font-semibold mb-1">{formatPrice(tableSlotInfo.event_booking_category.price, tableSlotInfo.event_booking_category.currency)}</div>
                        <p className="text-gray-500 whitespace-normal">{tableSlotInfo.event_booking_category.booking_category.description}</p>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-700"></div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          <div className="w-full h-12 bg-gradient-to-r from-amber-600/20 to-orange-600/20 border border-amber-500/30 rounded-lg flex items-center justify-center">
            <span className="text-amber-400 font-semibold tracking-widest">BAR</span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center justify-center gap-8 mt-12 text-sm text-gray-400">
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-gray-800 border-2 border-gray-700"></div><span>Available</span></div>
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-blue-600/40 border-2 border-blue-500"></div><span>Selected</span></div>
        <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-gray-900 border-2 border-gray-800 opacity-50"></div><span>Unavailable</span></div>
      </div>
    </div>
  );
};

export default TableLayout;
